import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { PublicFrame } from '../../components/Shell'
import { ErrorNote, Field, Spinner } from '../../components/ui'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n'

interface MfaState {
  aal: 'aal1' | 'aal2' | null
  factors: { id: string; status: 'verified' | 'unverified' }[]
}

/** Safe in-app path only (no open redirect through ?next=). */
function nextPath() {
  const n = new URLSearchParams(location.search).get('next') ?? '/'
  return n.startsWith('/') && !n.startsWith('//') ? n : '/'
}

export function MfaPage() {
  const { t } = useT()
  const qc = useQueryClient()
  const state = useQuery({ queryKey: ['mfa'], queryFn: () => api<MfaState>('/api/auth/mfa') })
  const verified = state.data?.factors.find((f) => f.status === 'verified')
  const enroll = useMutation({
    mutationFn: () =>
      api<{ factorId: string; qr: string; secret: string }>('/api/auth/mfa/enroll', { method: 'POST' }),
  })
  const [code, setCode] = useState('')
  const factorId = verified?.id ?? enroll.data?.factorId
  const verify = useMutation({
    mutationFn: () => api('/api/auth/mfa/verify', { body: { factorId, code } }),
    onSuccess: async () => {
      await qc.invalidateQueries()
      location.href = nextPath()
    },
  })
  if (state.isLoading) return <Spinner />
  const needsEnroll = !verified
  return (
    <PublicFrame>
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="eyebrow-ink mb-6">/ 2FA</div>
        <h1 className="h1 mb-4">{t('mfaTitle')}</h1>
        <p className="mb-8 text-body">{needsEnroll ? t('mfaSetupLead') : t('mfaChallengeLead')}</p>
        <div className="card card-pad grid gap-5">
          {needsEnroll && !enroll.data && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => enroll.mutate()}
              disabled={enroll.isPending}
            >
              {t('enable2fa')}
            </button>
          )}
          {needsEnroll && enroll.data && (
            <div className="grid gap-4">
              <div className="mx-auto rounded-lg bg-white p-3">
                <img src={enroll.data.qr} alt="QR" width={180} height={180} />
              </div>
              <Field label={t('mfaSecret')}>
                <div className="mono select-all break-all rounded-lg border border-hairline bg-canvas p-3 text-xs">
                  {enroll.data.secret}
                </div>
              </Field>
            </div>
          )}
          {(!needsEnroll || enroll.data) && (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                verify.mutate()
              }}
            >
              <Field label={t('mfaCode')}>
                <input
                  className="input mono text-center text-2xl tracking-[0.4em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </Field>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={verify.isPending || code.length !== 6}
              >
                {t('verify')}
              </button>
            </form>
          )}
          <ErrorNote error={enroll.error ?? verify.error} />
        </div>
      </div>
    </PublicFrame>
  )
}
